import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import FullWidthButton from "../components/buttons/FullWidthButton";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStaticOptions } from "../context/StaticOptionsContext";
import { useInvoiceSave } from "../context/InvoiceSaveContext";
import { dataService } from "../services/dataService";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import FloatingSelectInput from "../components/form_elements/FloatingSelectInput";

const languages = [
    { label: "Magyar", value: "hu" },
    { label: "Angol", value: "en" },
    { label: "Idegen", value: "foreign" },
];

const invoiceStatusLabels = {
    0: "Tervezet",
    1: "Feldolgozás alatt",
    5: "Kiegyenlítve",
};

const unwrapOption = (item) => {
    if (!item || typeof item !== "object") return item;
    let current = item;
    let guard = 0;
    while (
        current &&
        typeof current === "object" &&
        current.value &&
        typeof current.value === "object" &&
        guard < 5
    ) {
        current = current.value;
        guard += 1;
    }
    return current;
};

const toArray = (maybeCollection) => {
    if (!maybeCollection) return [];
    const base = Array.isArray(maybeCollection) ? maybeCollection : Object.values(maybeCollection);
    return base.map(unwrapOption);
};

const parseNumberInput = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
};

const formatAmount = (num) =>
    num == null || num === "" ? "" : (Math.round(Number(num) * 100) / 100).toFixed(2);

const formatAmountGrouped = (num) => {
    if (num == null || num === "") return "";
    const fixed = (Math.round(Number(num) * 100) / 100).toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${grouped}.${decPart}`;
};

const normalizeTaxes = (collection) =>
    toArray(collection)
        .map((item, idx) => {
            const percentRaw =
                item?.tax_degreee ??
                item?.tax_rate ??
                item?.degree ??
                item?.percent ??
                item?.value ??
                0;

            const percentNum = Number(percentRaw);
            if (Number.isNaN(percentNum)) return null;

            const percent = percentNum > 1 ? percentNum : percentNum * 100;
            const multiplier = percent / 100;

            return {
                id: String(item?.tax_szam ?? item?.tax_id ?? item?.id ?? `tax-${idx}`),
                multiplier,
                label: item?.tax_name ?? item?.label ?? `${percent.toFixed(2).replace(/\.00$/, "")}%`,
            };
        })
        .filter(Boolean);

const normalizePmodes = (collection) =>
    toArray(collection).map((item, idx) => {
        const number = Number(item?.pmode_szam ?? item?.value ?? idx);
        return {
            id: String(item?.pmode_szam ?? item?.id ?? `pmode-${idx}`),
            value: String(number),
            label: item?.pmode_name ?? item?.label ?? item?.name ?? "Ismeretlen",
            raw: item,
        };
    });

const formatCurrency = (value, currency = "HUF") =>
    value == null
        ? "-"
        : `${Number(value).toLocaleString("hu-HU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })} ${currency}`;

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("hu-HU");
};

const Szamlazas = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    // Teljes összegű támogatás (közgyógy, üzemi baleset): nincs térítési díj, ezért előlegszámla sem készül
    const isFullPriceAid = Number(order?.hmeliorder?.hmeliorder_specaidtype_is_fullpriceaid) === 1;
    const specAidTypeName = order?.hmeliorder?.hmeliorder_specaidtype_national || "";
    const { staticOptions } = useStaticOptions();
    const { setLoading: setInvoiceSaveLoading, setSuccessMessage: setInvoiceSaveSuccess } = useInvoiceSave();
    const [loading, setLoading] = useState(false);
    const [loadingRefresh, setLoadingRefresh] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [loadingReport, setLoadingReport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentReport, setCurrentReport] = useState({ reportType: "", fileNamePrefix: "", reportName: "", szamla_szam: "" });
    const [modalStep, setModalStep] = useState("form"); 
    const [modalMessage, setModalMessage] = useState(""); 
    const [modalOptions, setModalOptions] = useState({
        doDownload: true,
        doSendEmail: false,
        sendToPartner: true,
        customEmail: "",
        sendToCustom: true,
    });

    const taxes = useMemo(() => normalizeTaxes(staticOptions?.taxes), [staticOptions?.taxes]);
    const pmodes = useMemo(() => normalizePmodes(staticOptions?.pmodes), [staticOptions?.pmodes]);
    const vatOptions = useMemo(
        () => taxes.map((tax) => ({ value: tax.id, label: tax.label })),
        [taxes]
    );
    const pmodeOptions = useMemo(
        () => pmodes.map((pmode) => ({ value: pmode.value, label: pmode.label })),
        [pmodes]
    );
    const languageOptions = useMemo(
        () => languages.map((lang) => ({ value: lang.value, label: lang.label })),
        []
    );

    const [form, setForm] = useState({
        vatId: "",
        netAmount: "",
        grossAmount: "",
        note: "",
        pmode: "",
        deadline: "",
        language: languages[0].value,
        additionalNote: "",
        receiptType: "normal",
        technicalNote: "",
    });

    const getVatMultiplier = useCallback(
        (vatId) => taxes.find((tax) => tax.id === String(vatId))?.multiplier ?? 0,
        [taxes]
    );

    useEffect(() => {
        setForm((prev) => {
            let changed = false;
            const next = { ...prev };
            if (!prev.vatId && taxes.length) {
                next.vatId = taxes[0].id;
                changed = true;
            }
            if (!prev.pmode && pmodes.length) {
                next.pmode = pmodes[0].value;
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [taxes, pmodes]);

    const handleNetBlur = () => {
        const multiplier = getVatMultiplier(form.vatId);
        const numeric = parseNumberInput(form.netAmount);
        if (numeric == null) return;
        setForm((prev) => ({
            ...prev,
            netAmount: formatAmountGrouped(numeric),
            grossAmount: formatAmountGrouped(numeric * (1 + multiplier)),
        }));
    };

        const downloadPDF = (base64Data, fileName) => {
        try {
            const linkSource = `data:application/pdf;base64,${base64Data}`;
            const downloadLink = document.createElement("a");
            const currentDate = new Date().toISOString().slice(0, 10);

            downloadLink.href = linkSource;
            downloadLink.download = `${fileName}_${currentDate}.pdf`;
            downloadLink.click();
        } catch (error) {
            console.error("Hiba a PDF letöltése közben:", error);
            alert("Hiba történt a PDF letöltése során.");
        }
    };

    const handleConfirmModal = async () => {
            if (!order?.hmeliorder?.hmeliorder_szam) {
                setModalMessage("Hiányzó rendelésszám, nem lehet dokumentumot generálni!");
                return;
            }
    
            if (!modalOptions.doDownload && !modalOptions.doSendEmail) {
                setModalStep("error");
                setModalMessage("Kérjük, válasszon legalább egy műveletet!");
                return;
            }
    
            const { reportType, fileNamePrefix, szamla_szam } = currentReport;
            let emailRecipients = [];
    
            if (modalOptions.doSendEmail) {
                if (modalOptions.sendToPartner && order.partner?.partner_email) {
                    emailRecipients.push(order.partner.partner_email);
                }
                if (modalOptions.sendToCustom && modalOptions.customEmail) {
                    if (/^\S+@\S+\.\S+$/.test(modalOptions.customEmail)) {
                        emailRecipients.push(modalOptions.customEmail);
                    } else {
                        setModalStep("error");
                        setModalMessage("Kérjük, adjon meg egy érvényes egyedi e-mail címet!");
                        return;
                    }
                }
                if (emailRecipients.length === 0) {
                    setModalStep("error");
                    setModalMessage("Nincs kiválasztott címzett az e-mail küldéséhez!");
                    return;
                }
            }
    
            try {
                setModalStep("loading");
                setIsModalOpen(true);
    
                const reportData = {
                    report_type: reportType,
                    hmeliorder_szam: order.hmeliorder.hmeliorder_szam,
                    json: 1,
                    download: modalOptions.doDownload, 
                    email_to: emailRecipients, 
                    szamla_szam: szamla_szam,
                };
    
                const response = await dataService.getReport(reportData);
    
                if (response.data?.status === "ok") {
                    
                    if (modalOptions.doDownload && response.data?.data?.pdf_file_b64) {
                        downloadPDF(response.data.data.pdf_file_b64, `${fileNamePrefix}_${order.hmeliorder.hmeliorder_szam}`);
                    }
    
                    setModalStep("success");
                    setModalMessage(response.data?.message || "A művelet sikeresen végrehajtva!");
    
                    setTimeout(() => {
                        setIsModalOpen(false);
                    }, 2000); 
    
                } else {
                    throw new Error(response.data?.message || `Nem sikerült a(z) ${fileNamePrefix} generálása`);
                }
            } catch (err) {
                console.error(`Hiba a(z) ${fileNamePrefix} generálása közben:`, err);
                setModalStep("error");
                setModalMessage(`Hiba történt: ${err.message || "Ismeretlen hiba"}`);
            } finally {
                
                if (modalStep !== "success") {
                    setLoadingReport(false);
                }
            }
        };

    const handleGrossBlur = () => {
        const multiplier = getVatMultiplier(form.vatId);
        const numeric = parseNumberInput(form.grossAmount);
        if (numeric == null) return;
        setForm((prev) => ({
            ...prev,
            grossAmount: formatAmountGrouped(numeric),
            netAmount: formatAmountGrouped(multiplier === 0 ? numeric : numeric / (1 + multiplier)),
        }));
    };

    const handleVatChange = (value) => {
        const multiplier = getVatMultiplier(value);
        setForm((prev) => {
            const netNumeric = parseNumberInput(prev.netAmount);
            return {
                ...prev,
                vatId: value,
                grossAmount: netNumeric != null
                    ? formatAmountGrouped(netNumeric * (1 + multiplier))
                    : prev.grossAmount,
            };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "vatId") return handleVatChange(value);
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleReceiptToggle = (type) => {
        setForm((prev) => ({
            ...prev,
            receiptType: type,
            technicalNote: type === "technical" ? prev.technicalNote : "",
        }));
    };

      const openModalForReport = (reportType, fileNamePrefix, reportName, szamla_szam) => {
        setCurrentReport({ reportType, fileNamePrefix, reportName, szamla_szam });
        setModalStep("form");
        setModalMessage("");
        setModalOptions({
            doDownload: true,
            doSendEmail: false,
            sendToPartner: !!order.partner?.partner_email,
            customEmail: "",
            sendToCustom: true,
        });
        setIsModalOpen(true);
    };
      const handleModalOptionChange = (e) => {
        const { name, value, type, checked } = e.target;
        setModalOptions((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleTechnicalNoteChange = (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            technicalNote: value,
            receiptType: value.trim().length ? "technical" : "normal",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitSuccess("");

        const orderId = order?.hmeliorder?.hmeliorder_szam;
        const partnerId = order?.partner?.partner_szam;

        if (!orderId) {
            setSubmitError("Nincs aktív rendelés, ezért a számla nem menthető.");
            return;
        }

        if (isFullPriceAid) {
            setSubmitError("A rendelés 100%-os támogatású, térítési díj nincs, ezért számla nem készíthető.");
            return;
        }

        const netAmount = parseNumberInput(form.netAmount);
        const grossAmount = parseNumberInput(form.grossAmount);

        if (netAmount == null || grossAmount == null) {
            setSubmitError("A nettó és bruttó összeget kötelező kitölteni.");
            return;
        }

        if (netAmount < 0 || grossAmount < 0) {
            setSubmitError("A nettó és bruttó összeg nem lehet negatív.");
            return;
        }

        setLoading(true);
        setInvoiceSaveLoading(true);

        const calculatedPath = window.location.pathname.startsWith("/")
            ? window.location.pathname.slice(1)
            : window.location.pathname;

        try {
            console.log("Számla mentésének indítása:", { orderId, partnerId });

            const invoiceResponse = await dataService.saveInvoice({
                hmeliorder_szam: orderId,
                partner_szam: partnerId,
                invoice_form_data: {
                    vat_id: form.vatId,
                    net_amount: netAmount,
                    gross_amount: grossAmount,
                    note: form.note,
                    pmode: form.pmode,
                    deadline: form.deadline,
                    language: form.language,
                    additional_note: form.additionalNote,
                    receipt_type: form.receiptType,
                    technical_note: form.technicalNote,
                    save_path_source: calculatedPath,
                },
            });

            console.log("Számla mentés válasza:", invoiceResponse);

            const invoiceData = invoiceResponse?.data;
            if (!invoiceData) {
                throw new Error("Nincs válasz az API-tól.");
            }

            if (invoiceData.status === "error") {
                const apiMessage = invoiceData?.message || "Ismert hiba történt a számla mentésekor.";
                throw new Error(apiMessage);
            }

            const fullOrderData = invoiceData?.data?.full_hmeliorder_data;
            if (fullOrderData && typeof fullOrderData === "object") {
                const updatedInvoices = Array.isArray(fullOrderData?.szamlak)
                    ? fullOrderData.szamlak
                    : [];

                updateOrder((prev) => ({
                    ...prev,
                    ...fullOrderData,
                    szamlak: updatedInvoices,
                }));
            } else {
                console.warn("Az API válaszban nincs full_hmeliorder_data, az aktuális order marad");
            }

            setForm({
                vatId: "",
                netAmount: "",
                grossAmount: "",
                note: "",
                pmode: "",
                deadline: "",
                language: languages[0].value,
                additionalNote: "",
                receiptType: "normal",
                technicalNote: "",
            });

            setSubmitSuccess("Az előlegszámla létrehozása sikeres.");
            setInvoiceSaveSuccess("Az előlegszámla létrehozása sikeres!");
            setTimeout(() => {
                setSubmitSuccess("");
                setInvoiceSaveSuccess("");
            }, 3000);

        } catch (error) {
            let errorMessage = "Váratlan hiba történt a számla mentésekor.";

            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            console.error("Hiba a számla mentésekor:", {
                message: errorMessage,
                error: error,
                status: error?.response?.status,
                responseData: error?.response?.data,
            });

            setSubmitError(errorMessage);
        } finally {
            setLoading(false);
            setInvoiceSaveLoading(false);
        }
    };

    const location = useLocation();
    const backTarget = location.state?.from ?? '/RendelesAdatlap';
    const handleBackClick = () => navigate(backTarget);

    const handleRefreshInvoices = async () => {
        const orderId = order?.hmeliorder?.hmeliorder_szam;
        const partnerId = order?.partner?.partner_szam;

        if (!orderId || !partnerId) {
            setSubmitError("Nincs aktív rendelés adatok az frissítéshez.");
            return;
        }

        setLoadingRefresh(true);
        try {
            const orderResponse = await dataService.getOrder(orderId, partnerId);
            
            if (orderResponse?.data?.status === "ok" && orderResponse?.data?.data) {
                const latestOrder = orderResponse.data.data;
                updateOrder(() => latestOrder);
                setSubmitSuccess("A szamlak listája frissítve.");
                setTimeout(() => setSubmitSuccess(""), 3000);
            } else {
                throw new Error("Az order nem frissíthetett, próbáld később!");
            }
        } catch (error) {
            let errorMessage = "Hiba a szamlak lista frissítésekor.";
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            setSubmitError(errorMessage);
        } finally {
            setLoadingRefresh(false);
        }
    };

    const invoices = order?.szamlak ?? [];
    const orderedInvoices = [...invoices].sort(
        (a, b) => (b.szamla_dpaym ?? 0) - (a.szamla_dpaym ?? 0)
    );

    const formatPaymentMethod = (value) => {
        const match = pmodes.find((pm) => pm.value === String(value));
        return match ? match.label : "Ismeretlen";
    };

    return (
        <TablerLayout title="Számlázás" hideCloseButton>
            <div className="row">
                <div className="col-12 mb-4">
                    {isFullPriceAid ? (
                    <div className="card border-success">
                        <div className="card-body">
                            <div className="alert alert-success mb-0" role="alert">
                                <h4 className="alert-title">Nincs számlázandó térítési díj</h4>
                                <div>
                                    A rendelés támogatás típusa: <strong>{specAidTypeName || "támogatott"}</strong> (100%-os támogatás).
                                    Térítési díj nincs, ezért előlegszámla nem készíthető. A korábban kiállított számlák alább láthatók.
                                </div>
                            </div>
                        </div>
                    </div>
                    ) : (
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Új előlegszámla</h3>
                        </div>
                        <div className="card-body">
                            <form className="row g-3" onSubmit={handleSubmit}>
                                {!!submitError && (
                                    <div className="col-12">
                                        <div className="alert alert-danger mb-0" role="alert">
                                            {submitError}
                                        </div>
                                    </div>
                                )}
                                {!!submitSuccess && (
                                    <div className="col-12">
                                        <div className="alert alert-success mb-0" role="alert">
                                            {submitSuccess}
                                        </div>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <FloatingSelectInput
                                        label="ÁFA"
                                        id="vatId"
                                        name="vatId"
                                        value={form.vatId}
                                        onChange={handleChange}
                                        options={vatOptions}
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <FloatingLabelInput
                                        label="Előleg nettó összege"
                                        id="netAmount"
                                        name="netAmount"
                                        value={form.netAmount}
                                        onChange={handleChange}
                                        onBlur={handleNetBlur}
                                        inputMode="decimal"
                                        textAlign="right"
                                        type="text"
                                        addon="Forint"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <FloatingLabelInput
                                        label="Előleg bruttó összege"
                                        id="grossAmount"
                                        name="grossAmount"
                                        value={form.grossAmount}
                                        onChange={handleChange}
                                        onBlur={handleGrossBlur}
                                        inputMode="decimal"
                                        textAlign="right"
                                        type="text"
                                        addon="Forint"
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <FloatingLabelInput
                                        label="Megjegyzés a számlán"
                                        id="note"
                                        name="note"
                                        value={form.note}
                                        onChange={handleChange}
                                        multiline
                                        rows={2}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <FloatingLabelInput
                                        label="Egyéb megjegyzés"
                                        id="additionalNote"
                                        name="additionalNote"
                                        value={form.additionalNote}
                                        onChange={handleChange}
                                        multiline
                                        rows={2}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <FloatingSelectInput
                                        label="Fizetési mód"
                                        id="pmode"
                                        name="pmode"
                                        value={form.pmode}
                                        onChange={handleChange}
                                        options={pmodeOptions}
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <FloatingLabelInput
                                        label="Határidő"
                                        id="deadline"
                                        name="deadline"
                                        type="number"
                                        value={form.deadline}
                                        onChange={handleChange}
                                        textAlign="right"
                                        addon="nap"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <FloatingSelectInput
                                        label="Nyelv"
                                        id="language"
                                        name="language"
                                        value={form.language}
                                        onChange={handleChange}
                                        options={languageOptions}
                                        required
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label d-block mb-1">Nyugta típusa</label>
                                    <div className="d-flex gap-4">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="receipt-normal"
                                                checked={form.receiptType === "normal"}
                                                onChange={() => handleReceiptToggle("normal")}
                                                disabled={loading}
                                            />
                                            <label className="form-check-label" htmlFor="receipt-normal">
                                                Normál nyugta
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="receipt-technical"
                                                checked={form.receiptType === "technical"}
                                                onChange={() => handleReceiptToggle("technical")}
                                                disabled={loading}
                                            />
                                            <label className="form-check-label" htmlFor="receipt-technical">
                                                Technikai nyugta
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <FloatingLabelInput
                                        label="Technikai megjegyzés"
                                        id="technicalNote"
                                        name="technicalNote"
                                        value={form.technicalNote}
                                        onChange={handleTechnicalNoteChange}
                                        multiline
                                        rows={2}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="col-12">
                                    <FullWidthButton type="submit" plusClass="btn-success" disabled={loading}>
                                        {loading ? "Mentés..." : "Előlegszámla felvétele"}
                                    </FullWidthButton>
                                </div>
                            </form>
                        </div>
                    </div>
                    )}
                </div>

                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title mb-0">Előleg- és végszámlák</h3>
                            <div className="d-flex gap-2 align-items-center">
                                <span className="text-muted small">{invoices.length} db</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleRefreshInvoices}
                                    disabled={loadingRefresh || loading}
                                    title="Szamlak lista frissítése"
                                >
                                    {loadingRefresh ? (
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                    ) : (
                                        <i className="ti ti-refresh"></i>
                                    )}
                                    {loadingRefresh ? "Frissítés..." : "Lista frissítése"}
                                </button>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {invoices.length ? (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Számlaszám</th>
                                                <th>Típus</th>
                                                <th>Kiállítás dátuma</th>
                                                <th>Nettó</th>
                                                <th>ÁFA</th>
                                                <th>Bruttó</th>
                                                <th>Fizetési mód</th>
                                                {/* <th>Státusz</th> */}
                                                <th className="text-end">Műveletek</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderedInvoices.map((invoice) => (
                                                <tr key={invoice.szamla_szam}>
                                                    <td className="fw-semibold">{invoice.szamla_k2d_inv_no}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${
                                                                invoice.szamla_code === 'NOTO'
                                                                    ? "bg-warning text-dark"
                                                                    : "bg-success"
                                                            }`}
                                                        >
                                                            {invoice.szamla_code === 'NOTO' ? "Díjbekérő" : "Számla"}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(invoice.szamla_inv_date)}</td>
                                                    <td>{formatCurrency(invoice.szamla_netto, invoice.szamla_currency)}</td>
                                                    <td>{formatCurrency(invoice.szamla_vat, invoice.szamla_currency)}</td>
                                                    <td>{formatCurrency(invoice.szamla_total, invoice.szamla_currency)}</td>
                                                    <td>{formatPaymentMethod(invoice.szamla_pmode)}</td>
                                                    {/* <td className="pe-0">{invoiceStatusLabels[invoice.szamla_status] ?? `Státusz #${invoice.szamla_status ?? "-"}`}</td> */}
                                                    <td className="text-end ps-1">
                                                        <button className="btn btn-outline-secondary" onClick={() => openModalForReport("elit_invoice", "Számla", "Számla", invoice.szamla_szam)} disabled={loadingReport}>
                                                        {loadingReport ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <i className="ti ti-printer"></i>}
                                                        Számla nyomtatás
                                                    </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-4 text-center text-muted">Még nincs megjeleníthető számla.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
            {isModalOpen && (
                <div className="modal modal-blur fade show" tabIndex="-1" style={{ display: "block" }} onClick={modalStep !== "loading" ? () => setIsModalOpen(false) : undefined}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{currentReport.reportName} PDF riport</h5>
                                <button type="button" className="btn-close" onClick={modalStep !== "loading" ? () => setIsModalOpen(false) : undefined} disabled={modalStep === "loading"}></button>
                            </div>

                            {modalStep === "loading" && (
                                <div className="modal-body text-center py-5">
                                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                                    <h4>Művelet folyamatban...</h4>
                                    <p className="text-muted">Kérjük, várjon, a dokumentum generálása folyamatban van.</p>
                                </div>
                            )}

                            {modalStep === "success" && (
                                <div className="modal-body text-center py-5">
                                    <div className="mb-3 text-success">
                                        <i className="ti ti-circle-check" style={{ fontSize: "3rem" }}></i>
                                    </div>
                                    <h4>Sikeres művelet!</h4>
                                    <p className="text-muted">{modalMessage}</p>
                                </div>
                            )}

                            {modalStep === "error" && (
                                <div className="modal-body">
                                    <div className="text-center text-danger mb-3">
                                        <i className="ti ti-alert-circle" style={{ fontSize: "3rem" }}></i>
                                    </div>
                                    <div className="alert alert-danger text-center">
                                        <h4 className="alert-title">Hiba történt!</h4>
                                        {modalMessage}
                                    </div>
                                </div>
                            )}

                            {modalStep === "form" && (
                                <>
                                    <div className="modal-body">
                                        <p>Válassza ki a kívánt műveleteket:</p>
                                        <div className="row">
                                            <div className="col">
                                                <div className="form-check form-switch mb-3">
                                                    <input className="form-check-input" type="checkbox" name="doDownload" id="doDownloadCheck" checked={modalOptions.doDownload} onChange={handleModalOptionChange} />
                                                    <label className="form-check-label" htmlFor="doDownloadCheck">
                                                        <i className="ti ti-download me-2"></i>PDF letöltése
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="form-check form-switch mb-3">
                                                    <input className="form-check-input" type="checkbox" name="doSendEmail" id="doSendEmailCheck" checked={modalOptions.doSendEmail} onChange={handleModalOptionChange} />
                                                    <label className="form-check-label" htmlFor="doSendEmailCheck">
                                                        <i className="ti ti-mail me-2"></i>PDF küldése e-mailben
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {modalOptions.doSendEmail && (
                                            <div className="mt-3 border-top pt-3">
                                                <h6>E-mail címzettek:</h6>
                                                {order.partner?.partner_email ? (
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="checkbox" name="sendToPartner" id="sendToPartnerCheck" checked={modalOptions.sendToPartner} onChange={handleModalOptionChange} />
                                                        <label className="form-check-label" htmlFor="sendToPartnerCheck">
                                                            {order.partner.partner_name} (<code>{order.partner.partner_email}</code>)
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small">A partnerhez nem tartozik e-mail cím.</p>
                                                )}
                                                <div className="input-group mt-2">
                                                    <div className="input-group-text">
                                                        <input className="form-check-input mt-0" type="checkbox" name="sendToCustom" checked={modalOptions.sendToCustom} onChange={handleModalOptionChange} aria-label="Checkbox for following text input" />
                                                    </div>
                                                    <input type="email" className="form-control" name="customEmail" placeholder="Egyedi e-mail cím" value={modalOptions.customEmail} onChange={handleModalOptionChange} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn me-auto" onClick={() => setIsModalOpen(false)}>
                                            Mégse
                                        </button>
                                        <button type="button" className="btn btn-primary" onClick={handleConfirmModal} disabled={!modalOptions.doDownload && !modalOptions.doSendEmail}>
                                            <i className="ti ti-check me-2"></i>
                                            Műveletek végrehajtása
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Hiba esetén megjelenő footer */}
                            {modalStep === "error" && (
                                <div className="modal-footer">
                                    <button type="button" className="btn" onClick={() => setModalStep("form")}>
                                        <i className="ti ti-arrow-left me-2"></i>Vissza az űrlaphoz
                                    </button>
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
                                        Bezárás
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {isModalOpen && <div className="modal-backdrop fade show"></div>}
        </TablerLayout>
        
    );
};

export default Szamlazas;